import React, { useState, useEffect, useCallback } from 'react';
import { Badge, List, Spin, Typography, Empty, Button, Divider, Tag } from 'antd';
import {
  BellOutlined,
  CheckOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  SecurityScanOutlined,
  DollarOutlined,
  ApiOutlined,
  InfoCircleOutlined,
  BulbOutlined
} from '@ant-design/icons';
import { getNotifications, markAllNotificationsAsRead, NotificationHistoryItem } from '@/shared/api';
import { useAuthStore } from '@/store/auth';
import './NotificationPanel.less';

const { Text, Paragraph } = Typography;

interface NotificationPanelProps {
  onClose?: () => void;
}

const getUserId = (user: { _id?: string; id?: string } | null | undefined): string | null => {
  return user?._id || user?.id || localStorage.getItem('userId');
};

const NotificationPanel: React.FC<NotificationPanelProps> = ({ onClose }) => {
  const [notifications, setNotifications] = useState<NotificationHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const user = useAuthStore(state => state.user);
  const userId = getUserId(user);

  const fetchNotifications = useCallback(async (pageNum: number = 1) => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await getNotifications(userId, pageNum, 20);
      
      if (pageNum === 1) {
        setNotifications(response.data);
      } else {
        setNotifications(prev => [...prev, ...response.data]);
      }
      
      setHasMore(response.page < response.totalPages);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    setPage(1);
    fetchNotifications(1);
    
    const interval = setInterval(() => {
      fetchNotifications(1);
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleMarkAllAsRead = async () => {
    if (!userId) return;
    
    try {
      await markAllNotificationsAsRead(userId);
      setNotifications(prev => 
        prev.map(n => ({ ...n, read: true }))
      );
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchNotifications(nextPage);
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'billing':
        return <DollarOutlined style={{ color: '#52c41a' }} />;
      case 'integration':
      case 'deployments':
        return <ApiOutlined style={{ color: '#1890ff' }} />;
      case 'security':
        return <SecurityScanOutlined style={{ color: '#ff4d4f' }} />;
      case 'updates':
        return <BulbOutlined style={{ color: '#faad14' }} />;
      case 'marketing':
        return <InfoCircleOutlined style={{ color: '#722ed1' }} />;
      default:
        return <BellOutlined style={{ color: '#8c8c8c' }} />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'delivered':
        return <CheckOutlined style={{ color: '#52c41a' }} />;
      case 'failed':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'pending':
        return <ClockCircleOutlined style={{ color: '#faad14' }} />;
      default:
        return null;
    }
  };

  const getTimeAgo = (sentAt: string | Date) => {
    const date = new Date(sentAt);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    const weeks = Math.floor(days / 7);
    if (weeks < 4) return `${weeks}w ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months}mo ago`;
    const years = Math.floor(days / 365);
    return `${years}y ago`;
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  if (!userId) {
    return (
      <div className="notification-panel">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Sign in to view notifications"
          style={{ padding: '60px 20px' }}
        />
      </div>
    );
  }

  return (
    <div className="notification-panel">
      <div className="notification-header">
        <div className="header-title">
          <BellOutlined style={{ fontSize: 20 }} />
          <Text strong style={{ fontSize: 16, marginLeft: 8 }}>Notifications</Text>
          {unreadCount > 0 && (
            <Badge 
              count={unreadCount} 
              style={{ marginLeft: 8 }}
            />
          )}
        </div>
        {unreadCount > 0 && (
          <Button 
            type="link" 
            size="small" 
            onClick={handleMarkAllAsRead}
          >
            Mark all as read
          </Button>
        )}
      </div>

      <Divider style={{ margin: 0 }} />

      <div className="notification-list-container">
        {loading && page === 1 ? (
          <div className="notification-loading">
            <Spin size="large" />
          </div>
        ) : notifications.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No notifications yet"
            style={{ padding: '60px 20px' }}
          />
        ) : (
          <>
            <List
              dataSource={notifications}
              renderItem={(item) => (
                <List.Item
                  className={`notification-item ${!item.read ? 'unread' : ''}`}
                  key={item.id || item.messageId}
                >
                  <div className="notification-content">
                    <div className="notification-icon">
                      {getCategoryIcon(item.category)}
                    </div>
                    <div className="notification-body">
                      <div className="notification-header-row">
                        <Text strong className="notification-subject">
                          {item.subject}
                        </Text>
                        <div className="notification-meta">
                          <Tag 
                            color={
                              item.status === 'delivered' ? 'success' : 
                              item.status === 'failed' ? 'error' : 
                              'warning'
                            }
                            icon={getStatusIcon(item.status)}
                            style={{ marginRight: 0 }}
                          >
                            {item.status}
                          </Tag>
                        </div>
                      </div>
                      <Paragraph 
                        className="notification-message"
                        ellipsis={{ rows: 2 }}
                      >
                        {item.content}
                      </Paragraph>
                      <div className="notification-footer">
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {getTimeAgo(item.sentAt)}
                        </Text>
                        <Tag style={{ fontSize: 11, marginLeft: 8 }}>
                          {item.category}
                        </Tag>
                      </div>
                    </div>
                  </div>
                </List.Item>
              )}
            />
            {hasMore && (
              <div className="load-more-container">
                <Button 
                  type="link" 
                  onClick={loadMore}
                  loading={loading}
                >
                  Load More
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NotificationPanel;
